import { useCallback, useEffect, useState } from "react";
import classes from "./Captcha.module.scss";

interface ITurnStileRenderOptions {
  sitekey: string;
  theme: string;
  callback: (token: string) => void;
  "expired-callback": (code: string) => void;
  "error-callback": (code: string) => void;
}

interface ITurnStile {
  render: (id: string, options: ITurnStileRenderOptions) => string;
  reset: (identity: string) => void;
}

declare global {
  interface Window {
    turnstile?: ITurnStile;
    onloadCaptchaCallback: () => void;
  }
}

interface IProps {
  id: string;
  onSuccess: (token: string) => void;
  onFailed: (code: string) => void;
  onRendered: (reset: () => void) => void;
}

const Captcha = ({ id, onSuccess, onFailed, onRendered }: IProps) => {
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const render = useCallback(() => {
    try {
      if (!window.turnstile) {
        return;
      }

      const identity = window.turnstile.render(`#${id}`, {
        sitekey: import.meta.env.REACT_APP_CAPTCHA_KEY ?? "",
        theme: "dark",
        callback: onSuccess,
        "expired-callback": onFailed,
        "error-callback": onFailed
      });

      setWidgetId(identity);

      const reset = () => {
        window.turnstile?.reset(identity);
      };

      onRendered(reset);
    } catch (e) {
      console.warn(e);
      setIsError(true);
    }
  }, [id, onSuccess, onFailed, onRendered]);

  useEffect(() => {
    if (!window.turnstile) {
      window.onloadCaptchaCallback = render;
      loadScript();
    } else if (widgetId === null) {
      render();
    }
  }, [widgetId, render]);

  return (
    <div>
      <div id={id} className={classes.captcha} />
      {
        isError && (
          <div className={classes.error}>
            Sorry could not load captcha, please refresh the page.<br/>
            If that doesn't help, try to disable your ad blocker.
          </div>
        )
      }
    </div>
  );
};

const loadScript = () => {
  const js = document.createElement("script");

  js.async = true;
  js.defer = true;
  js.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadCaptchaCallback";

  document.head.appendChild(js);
};

export default Captcha;
